import { onMounted, ref, onUnmounted } from "vue";

import { defineStore } from "pinia";
import { getUserInfo, getNewBornInfo, getUserCoin } from "~/api/user";
import { containsAsterisk } from "~/utils/common";

export const useUserStore = defineStore("user", () => {
	const userInfo = ref({}); // 用户信息
	const isLogin = ref(false); // 登录状态
	/** 客户端已从本地同步过登录态，可用于避免 header 等先闪未登录 UI */
	const authReady = ref(false);
	const thirdBindEmail = ref(false); // 是否需要绑定邮箱（有些第三方登录获取不到手机邮箱）
	const thirdBindEmailParams = ref({
		login_type: "", //第三方登录type
		third_access_token: "", //授权token
		open_id: null, //line跟fb需要用到
	}); // 第三方绑定邮箱参数
	const userCouponsData = ref({});
	const userCreatedTime = ref("");
	const endTime = ref("");
	// const router = useRouter(); // Use Nuxt's navigateTo instead
	const freshFinishFlag = ref(false);

	function syncAuthFromStorage() {
		if (!import.meta.client || typeof localStorage === "undefined") {
			authReady.value = true;
			return;
		}
		isLogin.value = Boolean(localStorage.getItem("token"));
		authReady.value = true;
	}

	// 页面初始化，获取用户数据
	onMounted(async () => {
		if (!import.meta.client) return;

		// 在任何 await 之前同步 token，避免首屏先渲染登录按钮
		syncAuthFromStorage();

		const url = new URL(window.location.href);
		const path = url.pathname;
		let query = new URLSearchParams(url.search);
		query = Object.fromEntries(query.entries());

		if (query.token) {
			if (typeof localStorage !== "undefined") {
				localStorage.setItem("token", query.token);
			}
			delete query.token;
			syncAuthFromStorage();

			if (query.is_email) {
				delete query.is_email;
				await navigateTo({ path, query }, { replace: true });
				refreshLoginStatus().then(() => {
					freshFinishFlag.value = true;
				});
			} else {
				await navigateTo({ path, query }, { replace: true });
				refreshLoginStatus();
			}
		} else {
			refreshLoginStatus();
		}
		// 浏览器后退事件刷新登陆状态，兼容其他项目登陆后后退回来
		window.addEventListener("pageshow", () => {
			if (typeof localStorage !== "undefined" && localStorage.getItem("gwUser")) return;
			refreshLoginStatus();
		});
		// 监听token变化，token变化更新登陆状态
		window.addEventListener("storage", event => {
			if (event.key === "token") {
				refreshLoginStatus();
			}
		});
	});

	onUnmounted(() => {
		window.removeEventListener("pageshow", () => {});
		window.removeEventListener("storage", () => {});
	});

	// 检查登陆状态，同时获取用户信息
	async function refreshLoginStatus() {
		const url = new URL(window.location.href);
		const path = url.pathname;
		if (path == "/seer/guide-chat") {
			authReady.value = true;
			return;
		}
		syncAuthFromStorage();
		if (isLogin.value) {
			try {
				freshFinishFlag.value = false;
				const res = await getUserInfo();
				const newBornInfoData = await getNewBornInfo();
				const coinRes = await getUserCoin(); //获取用户铜币数据
				const { data } = res;
				const { data: coinData } = coinRes;
				const { user_coupons, user_created_time, end_time } = newBornInfoData.data;

				userCouponsData.value = user_coupons;
				userCreatedTime.value = user_created_time;
				endTime.value = end_time;

				// 用户信息
				const userRes = {
					avatar: data.avatar,
					nickname: data.nickname,
					birthday: data.birthday,
					sex: data.sex, //0=女,1=男,2=未知
					email: data.email,
					phone: data.phone,
					is_monthly_vip: data.is_monthly_vip, //是否是月费会员
					is_new_user: data.is_new_user, //是否是新用户
					coin_num: coinData.coin_num, //铜币数量
					coin_base: coinData.coin_base, //储值铜币数量
					coin_giving: coinData.coin_giving, //赠送可用铜币数量
				};
				userInfo.value = userRes;

				if (typeof localStorage !== "undefined") {
					localStorage.setItem("gwUser", JSON.stringify(userRes));

					// 部分第三方登录 授权登录阶段获取不到邮箱/手机号  缓存用于谷歌强化转化
					if (data.email && !containsAsterisk(data.email)) {
						localStorage.setItem("gw_token_email", data.email);
					}
					if (data.phone && !containsAsterisk(data.phone)) {
						localStorage.setItem("gw_token_phone", data.phone);
					}
				}
			} catch (error) {
				if (error?.code === 1007 || error?.code === 1010) {
					exit(false);
				}
			}
		}
	}

	// 退出登录
	function exit(isRedirect = true) {
		if (typeof localStorage !== "undefined") {
			localStorage.removeItem("token");
			localStorage.removeItem("gwUser");
			localStorage.removeItem("baziFile");
			localStorage.removeItem("gw_token_phone");
			localStorage.removeItem("gw_token_email");
			localStorage.removeItem("master_s_uid");
			localStorage.removeItem("master_s_token");
		}

		userInfo.value = {};
		isLogin.value = false;
		authReady.value = true;
		userCouponsData.value = {};
		userCreatedTime.value = 0;
		endTime.value = 0;

		// 只在客户端环境下进行页面跳转
		if (isRedirect && typeof window !== "undefined" && typeof location !== "undefined") {
			location.href = "/"; //直接刷新重置store数据
		}
	}

	// 修改 thirdBindEmail
	function setThirdBindEmail(status) {
		thirdBindEmail.value = status;
	}

	// 更新第三方登录绑定邮箱的参数
	function setThirdBindEmailParams({ login_type, third_access_token, open_id = null }) {
		thirdBindEmailParams.value = {
			login_type,
			third_access_token,
			open_id,
		};
	}

	return {
		isLogin,
		authReady,
		userInfo,
		thirdBindEmail,
		thirdBindEmailParams,
		userCouponsData,
		userCreatedTime,
		endTime,
		freshFinishFlag,
		refreshLoginStatus,
		exit,
		setThirdBindEmail,
		setThirdBindEmailParams,
	};
});
